import React from "react";
import SEO from "@/components/SEO";
import { useContent } from "@/context/ContentContext";
import { Mic } from "lucide-react";

function ytId(url) {
  const m = (url || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

export default function Podcast() {
  const { c } = useContent();
  const sidebar = c("podcast_sidebar");

  const raw = (c("podcast_episodes") || "").trim();
  let episodes = [];
  if (raw) {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) episodes = arr.map((e) => ({ id: ytId(e.url || ""), desc: (e.desc || "").trim() })).filter((e) => e.id);
    } catch {
      episodes = raw.split(/\n\s*-{3,}\s*\n/).map((block) => {
        const lines = block.trim().split("\n");
        const url = (lines.shift() || "").trim();
        return { id: ytId(url), desc: lines.join("\n").trim() };
      }).filter((e) => e.id);
    }
  }
  if (!episodes.length) {
    episodes = (c("podcast_videos") || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
      .map((u) => ({ id: ytId(u), desc: "" })).filter((e) => e.id);
  }

  return (
    <>
      <SEO title={c("podcast_title", "Podcast")} path="/podcast"
        description="The VoiceOverKids podcast — conversations about children's voice acting, casting and the industry." />
      <section className="bg-brand-ink text-white">
        <div className="container-x py-14 md:py-16">
          <span className="label-eyebrow inline-flex items-center gap-2 text-brand-purple"><Mic size={14} /> Podcast</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">{c("podcast_title", "The VoiceOverKids Podcast")}</h1>
          {c("podcast_intro") && <p className="mt-4 max-w-2xl whitespace-pre-line text-zinc-300">{c("podcast_intro")}</p>}
        </div>
      </section>

      <section className="container-x section-y">
        <div className={`grid gap-8 lg:gap-10 ${sidebar ? "lg:grid-cols-3" : ""}`}>
          <div className={sidebar ? "space-y-6 lg:col-span-2" : "space-y-6"}>
            {episodes.length === 0 ? (
              <div className="rounded-3xl border border-zinc-200 bg-white p-10 text-center text-zinc-600">
                Episodes coming soon.
              </div>
            ) : (
              episodes.map((ep, i) => (
                <div key={`${ep.id}-${i}`} data-testid={`podcast-episode-${i}`}
                  className={`grid items-start gap-5 rounded-3xl border border-zinc-200 bg-white p-4 md:p-5 ${ep.desc ? "md:grid-cols-2" : ""}`}>
                  <div className="aspect-video overflow-hidden rounded-2xl bg-black">
                    <iframe title={ep.id} src={`https://www.youtube.com/embed/${ep.id}?rel=0`} className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                  {ep.desc && <p className="whitespace-pre-line leading-relaxed text-zinc-600">{ep.desc}</p>}
                </div>
              ))
            )}
          </div>

          {sidebar && (
            <aside className="lg:col-span-1" data-testid="podcast-sidebar">
              <div className="sticky top-28 rounded-3xl border border-zinc-200 bg-brand-paper p-6">
                <h2 className="font-heading text-xl font-bold text-zinc-900">{c("podcast_sidebar_title", "About the show")}</h2>
                <p className="mt-3 whitespace-pre-line leading-relaxed text-zinc-600">{sidebar}</p>
              </div>
            </aside>
          )}
        </div>
      </section>
    </>
  );
}
